// SPDX-License-Identifier: GPL-3.0-or-later
// Score gate of the table rules. Every point goes through it.
//
// Why: the Durable Object clamps the score gain per second (MAX_SCORE_DELTA_PER_SEC).
// A big award (500,000 for Grand Approval) is more than that limit in one call.
// The gate pays a big award as a count-up: it never gives more than `capPerSecond`
// points in any window of 1000 ms. The rest waits in a queue (first in, first out).
// No point is lost: the queue pays out in later ticks, or in the end-of-ball bonus.

import type { RulesContext } from "../../shared/contracts.ts";

/** One award in the queue. */
interface Pending {
  points: number;
  readonly reason: string;
  readonly item: string | undefined;
}

/** A payment that the gate made. */
interface Paid {
  readonly time: number;
  readonly points: number;
}

/** Smallest part of an award that the gate pays on its own. It stops a count-up of tiny parts. */
const MIN_CHUNK = 1_000;

/** Window length in ms. */
const WINDOW_MS = 1_000;

export class ScoreGate {
  private paid: Paid[] = [];
  private queue: Pending[] = [];
  private requested = 0;

  constructor(private readonly capPerSecond: number) {}

  /** Clear all state. Call at game start. */
  reset(): void {
    this.paid = [];
    this.queue = [];
    this.requested = 0;
  }

  /** Total points that the rules asked for since `resetRequested`. Mode points use it. */
  get requestedTotal(): number {
    return this.requested;
  }

  resetRequested(): void {
    this.requested = 0;
  }

  /** Points in the queue that are not paid yet. */
  get pendingPoints(): number {
    let sum = 0;
    for (const p of this.queue) sum += p.points;
    return sum;
  }

  /** Ask for an award. The gate pays what the window allows now. */
  award(ctx: RulesContext, points: number, reason: string, item?: string): void {
    if (!Number.isFinite(points) || points <= 0) return;
    const whole = Math.floor(points);
    this.requested += whole;
    this.queue.push({ points: whole, reason, item });
    this.flush(ctx);
  }

  /** Pay from the queue as far as the window allows. Call each tick. */
  flush(ctx: RulesContext): void {
    if (this.queue.length === 0) return;
    const now = ctx.timeMs;
    // Keep every payment in the closed window [now - 1000, now]. Then any closed window
    // of 1000 ms holds at most `capPerSecond` points.
    this.paid = this.paid.filter((p) => p.time >= now - WINDOW_MS);
    let allowed = this.capPerSecond;
    for (const p of this.paid) allowed -= p.points;
    while (this.queue.length > 0 && allowed > 0) {
      const head = this.queue[0];
      if (!head) break;
      const take = Math.min(allowed, head.points);
      if (take < head.points && take < MIN_CHUNK) break;
      ctx.addScore(take, head.reason, head.item);
      this.paid.push({ time: now, points: take });
      allowed -= take;
      head.points -= take;
      if (head.points <= 0) this.queue.shift();
    }
  }

  /**
   * Remove up to `room` points from the front of the queue and return the sum.
   * The end-of-ball bonus uses it, so a count-up is not lost when the game ends.
   */
  takePending(room: number): number {
    let left = Math.max(0, Math.floor(room));
    let taken = 0;
    while (this.queue.length > 0 && left > 0) {
      const head = this.queue[0];
      if (!head) break;
      const take = Math.min(left, head.points);
      head.points -= take;
      taken += take;
      left -= take;
      if (head.points <= 0) this.queue.shift();
    }
    return taken;
  }
}
